import * as React from 'react';

import type { ClientBeneficiariesDiffusionDto } from '@/api/clients-beneficiaries-client';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

// =============================================================================
// clients-beneficiaries-section.tsx — Clients bénéficiaires diffusés sous réserve (S33, DBU §3.3).
// =============================================================================
// A0-6 OPTION 1 · DBU §3.3 l.2581/2588/2608/2648 : les clients bénéficiaires sont présentés en OUTPUT,
// mais la DIFFUSION DE LEUR NOM est conditionnée par le flag admin `campaign.clients_diffusion_enabled`
// (F0-DB-2, DEFAULT false = fail-closed). L'AUTORITÉ (lecture du flag, masquage) est l'API : ce
// composant est PUREMENT présentationnel et rend l'état porté par le DTO.
//
// DoD DISTINCTIVE (R3 / R2 L-053) : le rendu DÉPEND du booléen `diffusionEnabled` (le HANDLER, pas la
// copy) :
//   diffusionEnabled=false -> « communiqués ultérieurement » + cardinalité, AUCUN nom rendu ;
//   diffusionEnabled=true  -> liste des raisons sociales.
// L'assertion FRONT distingue les DEUX états. Le composant ne peut PAS « déduire » un nom masqué :
// quand la diffusion n'est pas autorisée, le DTO ne PORTE aucun nom (masquage côté serveur).
//
// WCAG 2.1 AA : <section> nommée (aria-labelledby), liste sémantique <ul>, message d'état role="status"
// (badge = renfort couleur, jamais seul porteur d'info ; le libellé texte porte l'information).

interface ClientsBeneficiariesSectionProps {
  /** Vue OUTPUT des bénéficiaires (diffusion déjà gate-ée par l'API). */
  readonly view: ClientBeneficiariesDiffusionDto;
}

/** Accord singulier/pluriel du libellé de cardinalité (« 1 client » / « N clients »). */
function beneficiaryCountLabel(count: number): string {
  return `${count} client${count > 1 ? 's' : ''} bénéficiaire${count > 1 ? 's' : ''}`;
}

export function ClientsBeneficiariesSection({
  view,
}: ClientsBeneficiariesSectionProps): React.JSX.Element {
  const headingId = 'clients-beneficiaires-heading';

  return (
    <section aria-labelledby={headingId}>
      <Card>
        <CardHeader>
          <CardTitle id={headingId}>Clients bénéficiaires</CardTitle>
          <CardDescription>
            {beneficiaryCountLabel(view.beneficiaryCount)} pour cette campagne. La diffusion de leur
            nom est autorisée par l'administrateur, campagne par campagne.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {view.diffusionEnabled ? (
            <DiffusedBeneficiaries view={view} />
          ) : (
            <MaskedBeneficiaries count={view.beneficiaryCount} />
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/** État DIFFUSÉ (flag ON) : la liste nommée des clients bénéficiaires. */
function DiffusedBeneficiaries({
  view,
}: {
  readonly view: ClientBeneficiariesDiffusionDto;
}): React.JSX.Element {
  if (view.beneficiaries.length === 0) {
    return (
      <p role="status" className="text-muted-foreground">
        Aucun client bénéficiaire n'est associé à cette campagne.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <Badge variant="secondary">Diffusion autorisée</Badge>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {view.beneficiaries.map((b) => (
          <li key={b.siren} className="flex items-baseline justify-between gap-4 px-4 py-3">
            <span className="font-medium">{b.raisonSociale}</span>
            <span className="font-mono text-sm text-muted-foreground">{b.siren}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** État MASQUÉ (flag OFF, défaut fail-closed) : cardinalité + « communiqués ultérieurement ». */
function MaskedBeneficiaries({ count }: { readonly count: number }): React.JSX.Element {
  return (
    <div className="space-y-3">
      <Badge variant="outline">Diffusion non autorisée</Badge>
      <p role="status" className="text-muted-foreground">
        {count > 0
          ? `${beneficiaryCountLabel(count)}. Les noms seront communiqués ultérieurement, sous réserve de l'autorisation de diffusion par l'administrateur.`
          : "Aucun client bénéficiaire n'est communiqué pour cette campagne."}
      </p>
    </div>
  );
}
